import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../../components/layout/NavBar';
import Footer from '../../components/layout/Footer';
import SearchFilters from '../properties/SearchFilters';
import PropertyGrid from '../properties/PropertyGrid';
import Login from '../auth/login/Login';
import Signup from '../auth/signup/Signup';
import { getProperties, getMapProperties } from '../../services/api';
import {
    FiMapPin, FiHome, FiUsers, FiShield, FiGrid, FiMap,
    FiArrowRight, FiSearch, FiMessageSquare, FiFileText, FiCheckCircle
} from 'react-icons/fi';

const MapView = lazy(() => import('../../components/MapView'));

/* ── Category pills data ────────────────────────────────── */
const CATEGORIES = [
    { label: 'Todos', tipo: '', emoji: '🏘️' },
    { label: 'Apartamentos', tipo: 'apartamento', emoji: '🏢' },
    { label: 'Casas', tipo: 'casa', emoji: '🏠' },
    { label: 'Habitaciones', tipo: 'habitacion', emoji: '🛏️' },
    { label: 'Estudios', tipo: 'estudio', emoji: '📐' },
    { label: 'Oficinas', tipo: 'oficina', emoji: '💼' },
    { label: 'Estudiantes', tipo: '', para_estudiantes: true, emoji: '🎓' },
];

/* ── How it works steps ─────────────────────────────────── */
const HOW_STEPS = [
    {
        icon: FiSearch,
        title: 'Busca tu hogar',
        desc: 'Filtra por ciudad, precio, tipo y amenidades. Más de 500 propiedades verificadas te esperan.',
        color: 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400',
        step: '01',
    },
    {
        icon: FiMessageSquare,
        title: 'Contacta al propietario',
        desc: 'Chatea directamente, agenda una visita y resuelve todas tus dudas antes de decidir.',
        color: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
        step: '02',
    },
    {
        icon: FiFileText,
        title: 'Firma tu contrato',
        desc: 'Genera y firma contratos digitales con validez legal. Todo desde la plataforma.',
        color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
        step: '03',
    },
    {
        icon: FiCheckCircle,
        title: '¡Arrienda seguro!',
        desc: 'Paga el arriendo online, reporta mantenimiento y gestiona todo desde tu panel.',
        color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
        step: '04',
    },
];

/* ── Home component ─────────────────────────────────────── */
const Home = () => {
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);
    const [isLoginView, setIsLoginView] = useState(true);
    const [propiedades, setPropiedades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({});
    const debounceRef = useRef(null);
    const [viewMode, setViewMode] = useState('grid');
    const [mapPins, setMapPins] = useState([]);
    const [loadingMap, setLoadingMap] = useState(false);
    const [activeCategory, setActiveCategory] = useState(0);

    const fetchProperties = useCallback(async (currentFilters) => {
        setLoading(true);
        try {
            const data = await getProperties(currentFilters);
            setPropiedades(data);
        } catch (error) {
            console.error('Error cargando propiedades:', error);
            setPropiedades([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const isStudentUrl = queryParams.get('para_estudiantes') === 'true';
        const initialFilters = isStudentUrl ? { para_estudiantes: true } : {};
        setFilters(initialFilters);
        fetchProperties(initialFilters);
    }, [location.search, fetchProperties]);

    useEffect(() => {
        if (viewMode === 'map' && mapPins.length === 0) {
            setLoadingMap(true);
            getMapProperties()
                .then(setMapPins)
                .catch(console.error)
                .finally(() => setLoadingMap(false));
        }
    }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchProperties(newFilters), 400);
    }, [fetchProperties]);

    const handleCategoryClick = (cat, idx) => {
        setActiveCategory(idx);
        const newFilters = { tipo: cat.tipo || '' };
        if (cat.para_estudiantes) newFilters.para_estudiantes = true;
        handleFilterChange(newFilters);
    };

    const openLogin = () => { setIsLoginView(true); setShowModal(true); };
    const openSignup = () => { setIsLoginView(false); setShowModal(true); };

    const stats = [
        { icon: FiHome, value: '500+', label: 'Propiedades' },
        { icon: FiMapPin, value: '25+', label: 'Ciudades' },
        { icon: FiUsers, value: '1.2K+', label: 'Usuarios' },
        { icon: FiShield, value: '100%', label: 'Verificadas' },
    ];

    return (
        <>
            <Helmet>
                <title>Hoommy — Arriendos en Colombia | Casas, Apartamentos y Habitaciones</title>
                <meta name="description" content="Encuentra tu próximo hogar en Colombia. Arriendos de casas, apartamentos, habitaciones y estudios verificados. Busca por ciudad, precio y tipo de propiedad." />
                <link rel="canonical" href="https://www.myhoommy.com/" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Hoommy — Arriendos en Colombia" />
                <meta property="og:description" content="Encuentra tu próximo hogar. Arriendos verificados de casas, apartamentos y habitaciones en las mejores ciudades de Colombia." />
                <meta property="og:url" content="https://www.myhoommy.com/" />
                <meta property="og:site_name" content="Hoommy" />
                <meta property="og:image" content="https://www.myhoommy.com/logoHoommy.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org", "@type": "WebSite",
                    "name": "Hoommy", "url": "https://www.myhoommy.com",
                    "description": "Plataforma de arriendos en Colombia",
                    "potentialAction": { "@type": "SearchAction", "target": "https://www.myhoommy.com/?q={search_term_string}", "query-input": "required name=search_term_string" }
                })}</script>
            </Helmet>

            <div className="min-h-screen flex flex-col bg-surface-50 dark:bg-surface-950 transition-colors duration-300">
                <Navbar openLoginModal={openLogin} openSignupModal={openSignup} />

                {/* ── Hero ─────────────────────────────────────────── */}
                <section className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-800 to-brand-950" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-600/20 via-transparent to-transparent" />
                    <div className="absolute top-10 right-10 w-32 h-32 rounded-full bg-brand-500/10 blur-3xl" />
                    <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full bg-purple-500/10 blur-3xl" />

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
                        {/* Split layout: text left, stats right */}
                        <div className="flex flex-col md:flex-row md:items-center md:gap-12">
                            <div className="flex-1 text-center md:text-left">
                                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white/90 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 border border-white/10">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    +500 propiedades verificadas
                                </span>
                                <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 tracking-tight leading-tight animate-fade-in-up">
                                    Encuentra tu{' '}
                                    <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
                                        hogar ideal
                                    </span>
                                    <br className="hidden sm:block" />
                                    <span className="text-white/80"> en Colombia</span>
                                </h1>
                                <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto md:mx-0 mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                                    Casas, apartamentos, habitaciones y estudios. Arriendos verificados en Bogotá, Medellín, Cali y 25+ ciudades más.
                                </p>
                                <div className="flex flex-wrap justify-center md:justify-start gap-3 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                                    <a href="#propiedades" className="flex items-center gap-2 btn-primary px-6 py-3 rounded-xl text-sm font-semibold shadow-lg shadow-brand-500/30">
                                        <FiSearch className="w-4 h-4" /> Buscar Propiedades
                                    </a>
                                    <button
                                        onClick={openSignup}
                                        className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all backdrop-blur-sm"
                                    >
                                        Publicar Propiedad <FiArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Stats cards — desktop side panel */}
                            <div className="hidden md:grid grid-cols-2 gap-3 shrink-0 w-72 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                                {stats.map(({ icon: Icon, value, label }) => (
                                    <div key={label} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-4 text-center">
                                        <div className="inline-flex p-2 rounded-xl bg-white/10 mb-2">
                                            <Icon className="w-5 h-5 text-white" />
                                        </div>
                                        <p className="text-2xl font-bold text-white">{value}</p>
                                        <p className="text-xs text-white/60">{label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Mobile stats */}
                        <div className="md:hidden flex flex-wrap justify-center gap-5 mt-8 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
                            {stats.map(({ icon: Icon, value, label }) => (
                                <div key={label} className="flex items-center gap-2 text-white/90">
                                    <div className="p-2 rounded-lg bg-white/10"><Icon className="w-4 h-4" /></div>
                                    <div className="text-left">
                                        <p className="text-base font-bold leading-none">{value}</p>
                                        <p className="text-[11px] text-white/60 mt-0.5">{label}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Wave */}
                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                            <path d="M0 60L60 55C120 50 240 40 360 35C480 30 600 30 720 33.3C840 36.7 960 43.3 1080 45C1200 46.7 1320 43.3 1380 41.7L1440 40V60H0Z"
                                className="fill-surface-50 dark:fill-surface-950" />
                        </svg>
                    </div>
                </section>

                {/* ── Category Pills ───────────────────────────────── */}
                <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-8 pb-2" id="propiedades">
                    <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                        {CATEGORIES.map((cat, idx) => (
                            <button
                                key={cat.label}
                                onClick={() => handleCategoryClick(cat, idx)}
                                className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 flex-shrink-0 ${activeCategory === idx
                                        ? 'bg-brand-600 border-brand-600 text-white shadow-md shadow-brand-500/20'
                                        : 'bg-white dark:bg-surface-900 border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400'
                                    }`}
                            >
                                <span className="text-base leading-none">{cat.emoji}</span>
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </section>

                {/* ── Search & Results ─────────────────────────────── */}
                <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
                    <h2 className="sr-only">Propiedades disponibles</h2>
                    <SearchFilters key={location.search} onFilterChange={handleFilterChange} activeFilters={filters} />

                    {/* View toggle + count */}
                    <div className="flex items-center justify-between mb-5 mt-2">
                        <p className="text-sm text-surface-500 dark:text-surface-400">
                            <span className="font-semibold text-surface-900 dark:text-white">{propiedades.length}</span>{' '}
                            propiedad{propiedades.length !== 1 ? 'es' : ''} encontrada{propiedades.length !== 1 ? 's' : ''}
                        </p>
                        <div className="flex gap-1 p-1 bg-surface-100 dark:bg-surface-800 rounded-xl">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'grid'
                                    ? 'bg-white dark:bg-surface-700 text-surface-900 dark:text-white shadow-sm'
                                    : 'text-surface-500 dark:text-surface-400'}`}
                            >
                                <FiGrid className="w-4 h-4" /> Tarjetas
                            </button>
                            <button
                                onClick={() => setViewMode('map')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'map'
                                    ? 'bg-white dark:bg-surface-700 text-surface-900 dark:text-white shadow-sm'
                                    : 'text-surface-500 dark:text-surface-400'}`}
                            >
                                <FiMap className="w-4 h-4" /> Mapa
                            </button>
                        </div>
                    </div>

                    {viewMode === 'map' ? (
                        <Suspense fallback={<div className="h-[520px] rounded-2xl skeleton" />}>
                            {loadingMap
                                ? <div className="h-[520px] rounded-2xl skeleton" />
                                : <MapView pins={mapPins} />}
                        </Suspense>
                    ) : (
                        <PropertyGrid propiedades={propiedades} loading={loading} />
                    )}
                </main>

                {/* ── Cómo funciona ────────────────────────────────── */}
                <section className="bg-white dark:bg-surface-900 border-t border-surface-100 dark:border-surface-800 transition-colors duration-300">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
                        <div className="text-center mb-12">
                            <span className="inline-block bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
                                Simple y seguro
                            </span>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-surface-900 dark:text-white">
                                ¿Cómo funciona Hoommy?
                            </h2>
                            <p className="text-surface-500 dark:text-surface-400 mt-3 max-w-xl mx-auto">
                                Desde la búsqueda hasta la firma del contrato, gestionamos todo en un solo lugar.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {HOW_STEPS.map(({ icon: Icon, title, desc, color, step }) => (
                                <div key={step} className="relative group text-center">
                                    {/* Step number connector line (desktop) */}
                                    <div className="relative inline-block mb-5">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto ${color} transition-transform duration-300 group-hover:scale-110`}>
                                            <Icon className="w-7 h-7" />
                                        </div>
                                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center shadow">
                                            {step}
                                        </span>
                                    </div>
                                    <h3 className="font-bold text-surface-900 dark:text-white mb-2">{title}</h3>
                                    <p className="text-surface-500 dark:text-surface-400 text-sm leading-relaxed">{desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Student CTA Banner ───────────────────────────── */}
                <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-10">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-brand-600 via-brand-500 to-purple-500 p-8 md:p-10">
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent pointer-events-none" />
                        <div className="relative flex flex-col md:flex-row items-center gap-6 justify-between">
                            <div className="text-center md:text-left">
                                <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                                    <span className="text-3xl">🎓</span>
                                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                                        Exclusivo Estudiantes
                                    </span>
                                </div>
                                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2">
                                    ¿Eres estudiante universitario?
                                </h2>
                                <p className="text-white/80 text-sm max-w-lg">
                                    Verifica tu carné y accede a propiedades exclusivas cerca de tu universidad. Hoommy para estudiantes: más seguro, más fácil.
                                </p>
                                <ul className="mt-4 flex flex-wrap justify-center md:justify-start gap-3">
                                    {['Propiedades verificadas', 'Cerca de tu U', 'Contratos estudiantiles', 'Pagos seguros'].map(b => (
                                        <li key={b} className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                                            <FiCheckCircle className="w-3.5 h-3.5 text-emerald-300" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex flex-col gap-3 w-full md:w-auto flex-shrink-0">
                                <Link
                                    to="/estudiantes"
                                    className="flex items-center justify-center gap-2 bg-white text-brand-600 font-bold py-3.5 px-8 rounded-2xl text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                                >
                                    Ver propiedades estudiantiles <FiArrowRight />
                                </Link>
                                <Link
                                    to="/verificar-estudiante"
                                    className="flex items-center justify-center gap-2 bg-white/15 backdrop-blur-sm hover:bg-white/25 border border-white/20 text-white font-semibold py-3 px-8 rounded-2xl text-sm transition-all"
                                >
                                    Verificar mi carné universitario
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <Footer />
            </div>

            {/* Modal */}
            {showModal && (
                <div
                    className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
                >
                    <div className="max-h-[90vh] overflow-y-auto animate-scale-in w-full max-w-md">
                        {isLoginView
                            ? <Login closeModal={() => setShowModal(false)} setIsLoginView={() => setIsLoginView(false)} />
                            : <Signup closeModal={() => setShowModal(false)} setIsLoginView={setIsLoginView} />
                        }
                    </div>
                </div>
            )}
        </>
    );
};

export default Home;
